"use client";

import { useState } from "react";
import { ExternalLink, ImageOff, Star } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { formatPrice } from "../../lib/format-price";

/**
 * Shape of a product card as emitted by the help-api chat tools (snake_case, matching the API).
 * Shared across the chat card components so the field set stays in one place. Display fields are
 * optional/nullable to mirror the nullable catalog columns.
 */
export interface ChatProduct {
  id: string;
  name: string;
  category?: string | null;
  price?: number | null;
  currency?: string;
  description?: string;
  url: string;
  image_url?: string;
  rating?: number | null;
}

interface ProductCardProps {
  product: ChatProduct;
}

export function ProductCard({ product }: ProductCardProps) {
  const [imgError, setImgError] = useState(false);
  const priceLabel = formatPrice(product.price, product.currency);

  return (
    <Card className="p-0 gap-0 overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 group">
      {/* Square well: its height follows the grid column width, which is what the canvas spec means
          by "square" — the artboards' hardcoded well heights are frame detail, not the contract. */}
      <div className="aspect-square relative bg-muted overflow-hidden">
        {imgError || !product.image_url ? (
          <div className="flex items-center justify-center w-full h-full bg-muted text-muted-foreground/40">
            <ImageOff className="h-10 w-10" />
          </div>
        ) : (
          <img
            src={product.image_url}
            alt={product.name}
            className="object-cover w-full h-full transition-transform duration-200 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      <CardContent className="flex flex-col p-4">
        {/* Reserved title slot: two lines of text-sm/leading-tight, so a one-line title and a
            two-line one put everything below them at the same offset across a grid row. The title
            owns the full row width — no category badge beside it: imported categories are long
            `A>B>C` breadcrumbs, and a non-shrinking badge squeezed the title out of the row. */}
        <div className="min-h-[35px]">
          <h3 className="font-semibold text-sm leading-tight line-clamp-2 break-words">
            <a
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground no-underline hover:underline hover:text-primary transition-colors"
            >
              {product.name}
            </a>
          </h3>
        </div>
        <p className="mt-2 min-h-[32px] text-xs text-muted-foreground line-clamp-2">
          {product.description}
        </p>
        {/* The reserved price-row height is load-bearing, not decoration: `price: null` is supported
            and formatPrice returns "" for it, so without it an unpriced card's action button would
            sit above a priced neighbour's and above the loading skeleton's matching slot. */}
        <div className="mt-3 flex h-7 items-center justify-between">
          {priceLabel ? (
            <span className="font-display text-xl font-bold text-primary">{priceLabel}</span>
          ) : (
            <span />
          )}
          {/* products.rating is null for every catalog-imported row (the Impact API carries no
              rating); only admin-entered products have one, so the star renders only then. */}
          {product.rating != null ? (
            // role="img" is what lets the label name the group: aria-label on a role-less div is not
            // exposed, and both children are aria-hidden, so without it the rating is never announced.
            <div
              role="img"
              className="flex items-center gap-1 text-sm text-muted-foreground"
              aria-label={`Rated ${product.rating.toFixed(1)} out of 5`}
            >
              <Star className="h-3.5 w-3.5 fill-warning text-warning" aria-hidden />
              <span className="font-medium" aria-hidden>{product.rating.toFixed(1)}</span>
            </div>
          ) : null}
        </div>
        <a
          href={product.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
        >
          View Product <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </CardContent>
    </Card>
  );
}
